import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import venueApi from '../../lib/venueApi';
import { Card } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { LayoutGrid, Loader2, ChevronDown, ChevronRight } from 'lucide-react';

export default function VenueLabOccupancyPage() {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const fetchData = () => {
    setLoading(true);
    venueApi
      .get('/lab-occupancy', { params: { months } })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [months]);

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  return (
    <div className="space-y-5" data-testid="venue-labs-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-violet-600" /> Lab Occupancy
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Master list of labs showing how many days are blocked in the selected window. <span className="text-slate-400">All days = calendar days · Wkdy = Mon–Fri (working days).</span>
          </p>
        </div>
        <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="venue-labs-months"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[3, 6, 9, 12].map((m) => <SelectItem key={m} value={String(m)}>{m} months</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-violet-600" /></div>
      ) : !data || data.labs.length === 0 ? (
        <Card className="p-8 text-center border border-border">
          <p className="text-sm text-slate-500">No labs configured yet.</p>
          <p className="text-xs text-slate-400 mt-1">Ask an admin to add labs from <span className="font-medium">Training → Masters → Labs</span>.</p>
        </Card>
      ) : (
        <Card className="border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-8" />
                <TableHead className="text-[10px] uppercase tracking-wider">Lab</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Location</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Capacity</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right" title={`All days: ${data.window.total_days} · Weekdays: ${data.window.total_weekdays}`}>Days Blocked<br/><span className="text-slate-400 normal-case font-normal">all · Mon–Fri</span></TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider w-[240px]" title={`Denominator = calendar days (${data.window.total_days}) and working days (${data.window.total_weekdays}) in the selected window`}>Utilization<br/><span className="text-slate-400 normal-case font-normal">all · Mon–Fri</span></TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Bookings</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.labs.map((row) => {
                const isOpen = !!expanded[row.lab_id];
                const util = row.utilization_pct;
                const utilWd = row.utilization_weekday_pct;
                const utilColor = util >= 60 ? 'bg-emerald-500' : util >= 30 ? 'bg-yellow-400' : 'bg-slate-300';
                const utilWdColor = utilWd >= 60 ? 'bg-emerald-500' : utilWd >= 30 ? 'bg-yellow-400' : 'bg-slate-300';
                return (
                  <React.Fragment key={row.lab_id}>
                    <TableRow className="hover:bg-slate-50 cursor-pointer" onClick={() => toggle(row.lab_id)} data-testid={`venue-lab-row-${row.lab_id}`}>
                      <TableCell>
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                      </TableCell>
                      <TableCell className="font-medium text-sm">{row.lab_number}</TableCell>
                      <TableCell className="text-sm text-slate-600">{row.location_name}</TableCell>
                      <TableCell className="text-sm text-right text-slate-500">{row.capacity || '—'}</TableCell>
                      <TableCell className="text-sm text-right font-semibold text-slate-800">
                        <div>{row.occupied_days}<span className="text-slate-400 text-[11px] font-normal"> / {row.total_days}</span></div>
                        <div className="text-[11px] text-slate-500 font-normal">{row.occupied_weekdays} / {row.total_weekdays} wkdy</div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2" title={`All days: ${row.occupied_days} of ${row.total_days}`}>
                            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <div className={`h-full ${utilColor}`} style={{ width: `${util}%` }} />
                            </div>
                            <span className="text-[10px] font-medium text-slate-600 w-14 text-right">{util}% all</span>
                          </div>
                          <div className="flex items-center gap-2" title={`Weekdays: ${row.occupied_weekdays} of ${row.total_weekdays}`}>
                            <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                              <div className={`h-full ${utilWdColor}`} style={{ width: `${utilWd}%` }} />
                            </div>
                            <span className="text-[10px] font-medium text-slate-700 w-14 text-right">{utilWd}% wkdy</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-right text-slate-500">{row.entries.length}</TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow>
                        <TableCell colSpan={7} className="bg-slate-50/70 p-0">
                          <div className="p-4">
                            {row.entries.length === 0 ? (
                              <p className="text-xs text-slate-400">No bookings in this window.</p>
                            ) : (
                              <div className="space-y-1.5">
                                {row.entries.map((e) => (
                                  <div key={e.id} className="flex items-center justify-between bg-white border border-slate-200 rounded-md px-3 py-2" data-testid={`venue-lab-entry-${e.id}`}>
                                    <div className="flex items-center gap-3 flex-1">
                                      <Badge className={e.status === 'confirmed' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-yellow-100 text-yellow-800 border-yellow-200'}>
                                        {e.status}
                                      </Badge>
                                      <span className="text-sm font-medium text-slate-800">{e.company_name}</span>
                                      <span className="text-[11px] text-slate-500">{e.from_date} → {e.to_date}</span>
                                      {e.total_strength ? <span className="text-[11px] text-slate-400">· {e.total_strength} pax</span> : null}
                                    </div>
                                    <Link to={`/venue/requirements/${e.id}`} className="text-[11px] text-violet-600 hover:underline">Details →</Link>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
